import React from 'react';
import { TrendingUp, BadgeCheck, Mail, Inbox, ArrowRightLeft } from 'lucide-react';

/**
 * SVG sparkline that renders a smoothed line chart of the last N daily lead counts.
 * Self-contained, no recharts dependency to keep the panel lightweight.
 */
const Sparkline = ({ data, width = 220, height = 48 }) => {
  if (!data || data.length === 0) return null;
  const counts = data.map((d) => d.count);
  const max = Math.max(...counts, 1);
  const stepX = width / Math.max(data.length - 1, 1);
  const points = counts.map((c, i) => {
    const x = i * stepX;
    const y = height - (c / max) * (height - 4) - 2;
    return [x, y];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  // Area fill path
  const areaPath = `${path} L${width.toFixed(1)},${height} L0,${height} Z`;
  const lastPoint = points[points.length - 1];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="none"
      data-testid="leads-sparkline"
      role="img"
      aria-label="Last 30 days of leads"
    >
      <defs>
        <linearGradient id="leads-spark-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C46A4A" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#C46A4A" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#leads-spark-grad)" />
      <path d={path} fill="none" stroke="#C46A4A" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastPoint[0]} cy={lastPoint[1]} r="2.5" fill="#C46A4A" />
    </svg>
  );
};

const StatTile = ({ icon: Icon, label, value, sub, testId }) => (
  <div
    className="bg-white border border-maroon/10 rounded-sm p-4 flex items-start gap-3"
    data-testid={testId}
  >
    <div className="rounded-sm bg-terracotta/10 text-terracotta p-2 shrink-0">
      <Icon size={18} />
    </div>
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wider text-maroon/50 font-semibold">{label}</p>
      <p className="text-2xl font-heading font-bold text-maroon leading-tight mt-0.5">{value}</p>
      {sub ? <p className="text-xs text-maroon/50 mt-0.5">{sub}</p> : null}
    </div>
  </div>
);

const LeadsStatsPanel = ({ stats }) => {
  if (!stats) return null;

  const total = stats.total || 0;
  const conv = stats.conversion_rate || 0;
  const convPct = (conv * 100).toFixed(total ? 1 : 0);
  const last30 = (stats.sparkline_30d || []).reduce((s, d) => s + (d.count || 0), 0);
  const status = stats.by_status || {};

  return (
    <div className="bg-sand/20 border border-maroon/10 rounded-sm p-4 mb-6" data-testid="leads-stats-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <StatTile
          icon={Inbox}
          label="Total leads"
          value={total}
          sub={`${last30} in last 30 days`}
          testId="stats-tile-total"
        />
        <StatTile
          icon={TrendingUp}
          label="Conversion rate"
          value={`${convPct}%`}
          sub={`${status.booked || 0} booked / ${total} total`}
          testId="stats-tile-conversion"
        />
        <StatTile
          icon={BadgeCheck}
          label="Member-linked"
          value={stats.members_linked || 0}
          sub="Leads tied to a customer account"
          testId="stats-tile-members"
        />
        <StatTile
          icon={ArrowRightLeft}
          label="From consultation"
          value={stats.from_consultation || 0}
          sub="Originated as a discovery inquiry"
          testId="stats-tile-from-consultation"
        />
        <StatTile
          icon={Mail}
          label="Invites sent"
          value={stats.invites_sent || 0}
          sub="Convert-to-member outreach"
          testId="stats-tile-invites"
        />
      </div>

      {/* Status breakdown + sparkline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-maroon/10 rounded-sm p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-maroon/60">Last 30 days</p>
            <p className="text-xs text-maroon/50">{last30} new {last30 === 1 ? 'lead' : 'leads'}</p>
          </div>
          <Sparkline data={stats.sparkline_30d || []} />
        </div>
        <div className="bg-white border border-maroon/10 rounded-sm p-3" data-testid="stats-status-breakdown">
          <p className="text-xs font-semibold uppercase tracking-wider text-maroon/60 mb-2">Status breakdown</p>
          <ul className="space-y-1.5 text-sm text-maroon">
            {[
              ['new', 'New', '#C46A4A'],
              ['contacted', 'Contacted', '#1C4E89'],
              ['quoted', 'Quoted', '#A07A4F'],
              ['booked', 'Booked', '#2F855A'],
              ['declined', 'Declined', '#6B7280'],
            ].map(([k, label, dot]) => (
              <li key={k} className="flex items-center justify-between" data-testid={`stats-status-${k}`}>
                <span className="inline-flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: dot }} />
                  {label}
                </span>
                <span className="font-semibold tabular-nums">{status[k] || 0}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LeadsStatsPanel;
